import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import {
  FileText,
  Plus,
  Clock,
  DollarSign,
  CheckCircle,
  XCircle,
  Award,
  Users,
  MapPin,
  Building2,
  Loader2,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RealtorTitleRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedRequest, setExpandedRequest] = useState(null);
  
  // Create form state
  const [formData, setFormData] = useState({
    transaction_id: "",
    service_type: "full_title",
    estimated_close_date: "",
    sale_price: "",
    loan_amount: "",
    lender_name: "",
    notes: "",
    preferred_timeline_days: "30"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [requestsRes, txnRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/realtor/title-requests`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/realtor/transactions`, { withCredentials: true })
      ]);
      setRequests(requestsRes.data);
      setTransactions(txnRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load title requests");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!formData.transaction_id || !formData.sale_price) {
      toast.error("Please select a transaction and enter sale price");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/realtor/title-requests`,
        {
          ...formData,
          sale_price: parseFloat(formData.sale_price),
          loan_amount: formData.loan_amount ? parseFloat(formData.loan_amount) : null,
          preferred_timeline_days: parseInt(formData.preferred_timeline_days)
        },
        { withCredentials: true }
      );
      toast.success("Title request posted to marketplace!");
      setCreateModalOpen(false);
      setFormData({
        transaction_id: "",
        service_type: "full_title",
        estimated_close_date: "",
        sale_price: "",
        loan_amount: "",
        lender_name: "",
        notes: "",
        preferred_timeline_days: "30"
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAwardBid = async (requestId, bidId, companyName) => {
    if (!confirm(`Award this contract to ${companyName}?`)) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/realtor/title-requests/${requestId}/award/${bidId}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Contract awarded to ${companyName}!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to award bid");
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!confirm("Are you sure you want to cancel this request?")) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/realtor/title-requests/${requestId}`,
        { withCredentials: true }
      );
      toast.success("Request cancelled");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel request");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open": return "bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]";
      case "pending": return "bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]";
      case "awarded": return "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]";
      case "cancelled": return "bg-red-500/20 text-[#FF3B30]";
      default: return "bg-white/[0.08] text-white/50";
    }
  };

  // Filter transactions that don't have active title requests
  const availableTransactions = transactions.filter(
    txn => !txn.title_request_id || txn.title_request_status === "cancelled"
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="realtor-title-requests">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Title Services</h1>
          <p className="text-white/50">Request title services and manage bids from title companies</p>
        </div>
        <Button
          onClick={() => setCreateModalOpen(true)}
          className="bg-gold hover:bg-gold/90 text-black"
          disabled={availableTransactions.length === 0}
        >
          <Plus className="w-4 h-4 mr-2" />
          Request Title Services
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Open Requests", value: requests.filter(r => r.status === "open").length, color: "text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" },
          { label: "Pending Bids", value: requests.filter(r => r.status === "pending" || (r.bids?.length > 0 && r.status === "open")).length, color: "text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]" },
          { label: "Awarded", value: requests.filter(r => r.status === "awarded").length, color: "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" },
          { label: "Total Requests", value: requests.length, color: "text-white" }
        ].map((stat, idx) => (
          <div key={idx} className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 rounded-2xl">
            <div className="text-sm text-white/50">{stat.label}</div>
            <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Requests List */}
      {requests.length === 0 ? (
        <div className="text-center py-12 bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08] rounded-2xl">
          <FileText className="w-12 h-12 text-white/30 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Title Requests</h3>
          <p className="text-white/50 mb-4">Post your first request to get bids from title companies</p>
          <Button
            onClick={() => setCreateModalOpen(true)}
            className="bg-gold hover:bg-gold/90 text-black"
            disabled={availableTransactions.length === 0}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Request
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <div
              key={request.request_id}
              className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl overflow-hidden"
            >
              {/* Request Header */}
              <div
                className="p-5 cursor-pointer hover:bg-white/[0.04]"
                onClick={() => setExpandedRequest(expandedRequest === request.request_id ? null : request.request_id)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <MapPin className="w-4 h-4 text-gold" />
                      <h3 className="font-semibold text-white">{request.property_address}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded ${getStatusColor(request.status)}`}>
                        {request.status?.toUpperCase()}
                      </span>
                      {request.bids?.length > 0 && (
                        <span className="px-2 py-0.5 text-xs rounded bg-gold/20 text-gold">
                          {request.bids.length} bid{request.bids.length > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <div className="text-xs text-white/40">Sale Price</div>
                        <div className="font-mono font-bold text-white">${request.sale_price?.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-white/40">Service Type</div>
                        <div className="text-white">{request.service_type?.replace("_", " ")}</div>
                      </div>
                      <div>
                        <div className="text-xs text-white/40">Timeline</div>
                        <div className="text-white">{request.preferred_timeline_days} days</div>
                      </div>
                      <div>
                        <div className="text-xs text-white/40">Close Date</div>
                        <div className="text-white">{request.estimated_close_date || "TBD"}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {request.status === "awarded" && (
                      <div className="text-right">
                        <div className="text-xs text-white/40">Awarded to</div>
                        <div className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] font-medium">{request.awarded_company_name}</div>
                      </div>
                    )}
                    {expandedRequest === request.request_id ? (
                      <ChevronUp className="w-5 h-5 text-white/50" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-white/50" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Bids Section */}
              {expandedRequest === request.request_id && (
                <div className="border-t border-white/[0.08] p-5 bg-[#0A0A0A]">
                  {request.status !== "awarded" && request.status !== "cancelled" && (
                    <div className="flex justify-end mb-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCancelRequest(request.request_id)}
                        className="border-red-500/50 text-[#FF3B30] hover:bg-red-500/10"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancel Request
                      </Button>
                    </div>
                  )}
                  
                  {(!request.bids || request.bids.length === 0) ? (
                    <div className="text-center py-8">
                      <Users className="w-10 h-10 text-white/30 mx-auto mb-3" />
                      <p className="text-white/50">No bids yet. Title companies will see your request in the marketplace.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-white/50 uppercase tracking-wider">Bids Received</h4>
                      {request.bids.map((bid) => (
                        <div
                          key={bid.bid_id}
                          className={`bg-black border p-4 rounded-2xl ${
                            bid.status === "awarded" ? "border-emerald-500/50" : "border-white/[0.08]"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <Building2 className="w-4 h-4 text-white/50" />
                                <span className="font-medium text-white">{bid.company_name}</span>
                                {bid.status === "awarded" && (
                                  <span className="px-2 py-0.5 text-xs rounded bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">
                                    <Award className="w-3 h-3 inline mr-1" />
                                    AWARDED
                                  </span>
                                )}
                              </div>
                              
                              <div className="grid grid-cols-4 gap-3 mb-2 text-sm">
                                <div>
                                  <span className="text-white/40">Title Search:</span>
                                  <span className="ml-2 text-white">${bid.title_search_fee?.toLocaleString()}</span>
                                </div>
                                <div>
                                  <span className="text-white/40">Closing:</span>
                                  <span className="ml-2 text-white">${bid.closing_fee?.toLocaleString()}</span>
                                </div>
                                <div>
                                  <span className="text-white/40">Insurance:</span>
                                  <span className="ml-2 text-white">${bid.title_insurance_premium?.toLocaleString()}</span>
                                </div>
                                <div>
                                  <span className="text-white/40">Timeline:</span>
                                  <span className="ml-2 text-white">{bid.estimated_completion_days} days</span>
                                </div>
                              </div>
                              
                              {bid.message && (
                                <p className="text-sm text-white/50 italic">"{bid.message}"</p>
                              )}
                            </div>
                            
                            <div className="text-right">
                              <div className="text-xs text-white/40 mb-1">Total Fee</div>
                              <div className="text-xl font-mono font-bold text-gold mb-2">
                                ${bid.total_fee?.toLocaleString()}
                              </div>
                              
                              {request.status === "open" && bid.status === "pending" && (
                                <Button
                                  size="sm"
                                  onClick={() => handleAwardBid(request.request_id, bid.bid_id, bid.company_name)}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Award
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Request Modal */}
      {createModalOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/[0.08]">
              <h2 className="text-xl font-bold text-white">Request Title Services</h2>
              <p className="text-white/50 text-sm mt-1">Post to marketplace for title companies to bid</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-white/50 mb-1">Select Transaction *</label>
                <select
                  value={formData.transaction_id}
                  onChange={(e) => {
                    const txn = transactions.find(t => t.transaction_id === e.target.value);
                    setFormData({
                      ...formData,
                      transaction_id: e.target.value,
                      sale_price: txn?.sale_price?.toString() || ""
                    });
                  }}
                  className="w-full px-3 py-2 bg-black border border-white/[0.08] rounded text-white"
                >
                  <option value="">Select a transaction...</option>
                  {availableTransactions.map((txn) => (
                    <option key={txn.transaction_id} value={txn.transaction_id}>
                      {txn.property_address} - {txn.buyer_name || "No buyer"}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-white/50 mb-1">Service Type</label>
                <select
                  value={formData.service_type}
                  onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                  className="w-full px-3 py-2 bg-black border border-white/[0.08] rounded text-white"
                >
                  <option value="full_title">Full Title Services</option>
                  <option value="title_search_only">Title Search Only</option>
                  <option value="closing_only">Closing Only</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/50 mb-1">Sale Price *</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <Input
                      type="number"
                      value={formData.sale_price}
                      onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                      className="pl-9 bg-black border-white/[0.08]"
                      placeholder="350000"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-white/50 mb-1">Loan Amount</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <Input
                      type="number"
                      value={formData.loan_amount}
                      onChange={(e) => setFormData({ ...formData, loan_amount: e.target.value })}
                      className="pl-9 bg-black border-white/[0.08]"
                      placeholder="280000"
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/50 mb-1">Lender Name</label>
                  <Input
                    value={formData.lender_name}
                    onChange={(e) => setFormData({ ...formData, lender_name: e.target.value })}
                    className="bg-black border-white/[0.08]"
                    placeholder="ABC Mortgage"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/50 mb-1">Timeline (days)</label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <Input
                      type="number"
                      value={formData.preferred_timeline_days}
                      onChange={(e) => setFormData({ ...formData, preferred_timeline_days: e.target.value })}
                      className="pl-9 bg-black border-white/[0.08]"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-white/50 mb-1">Expected Close Date</label>
                <Input
                  type="date"
                  value={formData.estimated_close_date}
                  onChange={(e) => setFormData({ ...formData, estimated_close_date: e.target.value })}
                  className="bg-black border-white/[0.08]"
                />
              </div>
              
              <div>
                <label className="block text-sm text-white/50 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-black border border-white/[0.08] rounded text-white placeholder-white/30 resize-none"
                  rows={3}
                  placeholder="Any special requirements or notes for title companies..."
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-white/[0.08] flex gap-3">
              <Button
                variant="outline"
                onClick={() => setCreateModalOpen(false)}
                className="flex-1 border-white/[0.1]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateRequest}
                disabled={submitting || !formData.transaction_id || !formData.sale_price}
                className="flex-1 bg-gold hover:bg-gold/90 text-black"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Posting...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Post Request
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
